import loadable from "@loadable/component";
import eventBus from "@sd-utility/eventbus";
import useGlobalConfig from "client/context/GlobalConfig";
import useSlotConfig from "client/context/SlotConfig";
import { GRID_UPDATE_EVENT } from "client/utils/consts";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { createRoot, Root } from "react-dom/client";
import { AmpInGridBannerConfig, AmpInGridBanners, AmpPersonalContentBannersContainer } from "types/amplience";
import type { ContentBanner1Props } from "../ContentBanner1/ContentBanner1";
import { isPersonalisedContainer } from "./utils";

export interface InGridBannersProps extends Partial<ContentBanner1Props>, React.HTMLAttributes<HTMLDivElement> {}

const ContentBanner1 = loadable(
  () => import(/* webpackChunkName: "ContentBanner1" */ "../ContentBanner1/ContentBanner1"),
  { ssr: false },
);

const ContentBanner1PersonalContainer = loadable(
  () => import(/* webpackChunkName: "ContentBanner1" */ "../ContentBanner1/PersonalContainer"),
  { resolveComponent: (m) => m.ContentBanner1PersonalContainer, ssr: false },
);

const getInGridLocs = () =>
  [...document.querySelectorAll<HTMLDivElement>("div[data-content-in-grid-banner='true']")].map((el) => {
    el.querySelector("#content-in-grid-root")?.remove();

    const rootEl = document.createElement("div");
    rootEl.id = "content-in-grid-root";
    el.appendChild(rootEl);

    return {
      container: el,
      root: createRoot(rootEl),
    };
  });

const BannerPosMap: Record<keyof AmpInGridBanners, number> = {
  position1: 0,
  position2: 1,
  position3: 2,
  position4: 3,
};

export const InGridBanners: React.FC<InGridBannersProps> = () => {
  const { brand } = useGlobalConfig();
  const { inGridBanners } = useSlotConfig();
  const [containerEls, setContainerEls] = useState<ReturnType<typeof getInGridLocs> | null>(null);

  const containerElsRef = useRef(containerEls);
  containerElsRef.current = containerEls;

  const removeBanner = useCallback((container: HTMLDivElement, root: Root) => {
    try {
      const hasBanner = container.querySelector("[data-testid='content-content-banner']");
      if (!hasBanner) return;

      container.classList.add("grid-item--hidden");
      root.render(<></>); // removes banner without unmounting node
      container.removeAttribute("data-inGrid-content-banner");
    } catch (error) {
      console.debug(error);
    }
  }, []);

  const unmountRoots = useCallback(() => {
    containerElsRef?.current?.forEach(({ container, root }) => {
      try {
        root.unmount();
        container.querySelector("#content-in-grid-root")?.remove();
      } catch (error) {
        console.debug(error);
      }
    });
  }, []);

  useEffect(() => {
    const subscription = eventBus.subscribe(GRID_UPDATE_EVENT, () => {
      // timeout due to unmount warning
      setTimeout(() => {
        unmountRoots();
        setContainerEls(getInGridLocs());
      });
    });

    return () => subscription.unsubscribe();
  }, [unmountRoots]);

  useEffect(
    () => () => {
      setTimeout(unmountRoots);
    },
    [unmountRoots],
  );

  useEffect(() => {
    if (!inGridBanners) return;

    const unmountHandlers = (
      Object.entries(inGridBanners) as Array<
        [key: string, banner: AmpInGridBannerConfig | AmpPersonalContentBannersContainer | undefined]
      >
    ).map(([key, banner]) => {
      const position = BannerPosMap[key];
      if (typeof position !== "number") return;

      const { container, root } = containerEls?.[position] ?? {};
      if (!container || !root || container.querySelector('[data-testid="recommendations-banner"]')) return;

      // timeout due to render warning
      setTimeout(() => {
        try {
          // remove any existing banner
          removeBanner(container, root);

          // If there is no banner, skip rendering
          if (!banner) return;

          container.setAttribute("data-inGrid-content-banner", "true");
          root.render(
            isPersonalisedContainer(banner) ? (
              <ContentBanner1PersonalContainer brand={brand} config={banner} inGrid maxBanners={1} />
            ) : (
              <ContentBanner1 brand={brand} config={banner} inGrid />
            ),
          );
          container.classList.remove("grid-item--hidden");
        } catch (error) {
          console.debug(error);
        }
      });

      return () => {
        setTimeout(() => removeBanner(container, root));
      };
    });

    return () => unmountHandlers.forEach((u) => u?.());
  }, [brand, containerEls, inGridBanners, removeBanner]);

  return <></>;
};
